// Renders the "Household" field for create modals that need to land a
// record on a household — search-and-pick, or "Can't find them? + Add a
// new household" in-flow. Pairs with the useHouseholdPicker hook, which
// owns all the state and side effects this just displays. Extracted from
// NewOpportunityModal so New Transaction can offer the identical flow.

import type { CSSProperties } from "react";

import type { HouseholdListItem } from "../api";
import { Button } from "../ui";
import { DuplicateContactWarning } from "./DuplicateContactWarning";
import type { HouseholdPickerController } from "./use-household-picker";

/**
 * A second line under a household's name in the search results — "Villegas
 * Household" alone is indistinguishable when several households share a
 * surname (Grant, 2026-08-30 BrokerMint audit: 4 identical "Villegas
 * Household" rows in this exact picker). `api.listHouseholds` already
 * returns `members_label` (who's actually in it, e.g. "Tom & Carolyn
 * Keller") and the primary address's city/state, so no server change is
 * needed — this was purely a missing render. Falls back to city/state when
 * there's no members_label (a household with no named member yet), and to
 * nothing when the search response carries neither.
 */
function householdResultSubline(h: HouseholdListItem): string | null {
  const parts: string[] = [];
  if (h.members_label) parts.push(h.members_label);
  const cityState = [h.primary_city, h.primary_state].filter(Boolean).join(", ");
  if (cityState) parts.push(cityState);
  return parts.length > 0 ? parts.join(" · ") : null;
}

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--fg-3, #6b7785)",
  margin: "14px 0 4px",
};
const controlStyle: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  borderRadius: 6,
  border: "1px solid var(--mid-gray, #e0e6ec)",
  boxSizing: "border-box",
};

export function HouseholdPickerField({
  picker,
  autoFocus,
}: {
  picker: HouseholdPickerController;
  /** Autofocus the search box (or the new-household form's first field)
   *  when this is the modal's first field. */
  autoFocus?: boolean;
}) {
  return (
    <>
      <label style={labelStyle}>Household</label>
      {picker.addingHousehold ? (
        <div
          style={{
            border: "1px solid var(--mid-gray, #e0e6ec)",
            borderRadius: 6,
            padding: "10px 10px 4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-3, #6b7785)" }}>
              New household
            </span>
            <Button variant="ghost" onClick={picker.cancelAddingHousehold}>
              Search instead
            </Button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              style={{ ...controlStyle, marginTop: 8 }}
              placeholder="First name"
              value={picker.newFirstName}
              onChange={(e) => picker.setNewFirstName(e.target.value)}
              autoFocus={autoFocus}
            />
            <input
              type="text"
              style={{ ...controlStyle, marginTop: 8 }}
              placeholder="Last name"
              value={picker.newLastName}
              onChange={(e) => picker.setNewLastName(e.target.value)}
            />
          </div>
          <input
            type="email"
            style={{ ...controlStyle, marginTop: 8 }}
            placeholder="Email (optional)"
            value={picker.newEmail}
            onChange={(e) => picker.setNewEmail(e.target.value)}
          />
          <input
            type="tel"
            style={{ ...controlStyle, margin: "8px 0 10px" }}
            placeholder="Phone (optional)"
            value={picker.newPhone}
            onChange={(e) => picker.setNewPhone(e.target.value)}
          />
          <DuplicateContactWarning
            matches={picker.dupMatches}
            input={{
              legal_first_name: picker.newFirstName,
              legal_last_name: picker.newLastName,
            }}
            onUseHousehold={picker.useExistingHousehold}
            onConfirmDifferentPerson={picker.confirmDifferentPerson}
          />
        </div>
      ) : picker.picked ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            border: "1px solid var(--mid-gray, #e0e6ec)",
            borderRadius: 6,
          }}
        >
          <span style={{ fontWeight: 600, color: "var(--navy, #022c51)" }}>
            {picker.picked.display_name}
          </span>
          <Button
            variant="ghost"
            onClick={() => {
              picker.setPicked(null);
              picker.setQuery("");
            }}
          >
            Change
          </Button>
        </div>
      ) : (
        <>
          <input
            type="search"
            style={controlStyle}
            placeholder="Search households…"
            value={picker.query}
            onChange={(e) => picker.setQuery(e.target.value)}
            autoFocus={autoFocus}
          />
          {picker.results.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                margin: "6px 0 0",
                padding: 0,
                border: "1px solid var(--mid-gray, #e0e6ec)",
                borderRadius: 6,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {picker.results.map((h) => {
                const subline = householdResultSubline(h);
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => picker.setPicked(h)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        background: "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--light-gray, #f4f6f8)",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      <span style={{ display: "block" }}>{h.display_name}</span>
                      {subline && (
                        <span
                          style={{
                            display: "block",
                            fontSize: 12,
                            color: "var(--fg-3, #6b7785)",
                            marginTop: 2,
                          }}
                        >
                          {subline}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => {
              picker.setAddingHousehold(true);
              picker.setQuery("");
            }}
            style={{
              display: "block",
              marginTop: 6,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "var(--blue, #4e98c6)",
            }}
          >
            Can't find them? + Add a new household
          </button>
        </>
      )}
    </>
  );
}
